import React, { useState, useEffect } from 'react';
import { Handle, Position } from '@xyflow/react';
import { Popconfirm, Modal, message, Tag, Card, Select, Input, Button, Space, Typography, Table, Tabs, Switch, InputNumber, Alert, Spin } from 'antd';
import { ApiOutlined, QuestionCircleOutlined, DeleteOutlined, PlusOutlined, MinusCircleOutlined, LockOutlined, SendOutlined, CopyOutlined } from '@ant-design/icons';
import NodeInputHandler from '../common/NodeInputHandler';
import GenericNodeContent from '../common/GenericNodeContent';
import { adminInstance } from '../api/axiosConfig';

// Helper function for safe array initialization
const ensureArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value === null || value === undefined) return [];
  if (typeof value === 'object') return [];
  return [];
};

// Helper function for safe object initialization
const ensureObject = (value, defaultObj = {}) => {
  if (value && typeof value === 'object' && !Array.isArray(value)) return value;
  return defaultObj;
};

const ExtractionHelpModal = ({ visible, onClose }) => {
  return (
    <Modal
      title="Data Extraction Guide"
      open={visible}
      onCancel={onClose}
      footer={[
        <Button key="close" type="primary" onClick={onClose}>
          Got it!
        </Button>
      ]}
      width={700}
    >
      <Space direction="vertical" style={{ width: '100%' }}>
        <Title level={4}>Understanding Data Extraction</Title>
        <Paragraph>
          The extraction pattern helps you get specific data from API responses. Think of it like a path 
          to find exactly what you want in the response data.
        </Paragraph>

        <Title level={4}>Basic Patterns</Title>
        <Table
          size="small"
          pagination={false}
          columns={[
            { title: 'Pattern', dataIndex: 'pattern', key: 'pattern' },
            { title: 'What It Does', dataIndex: 'desc', key: 'desc' },
            { title: 'Example Result', dataIndex: 'example', key: 'example' }
          ]}
          dataSource={[
            {
              key: '1',
              pattern: 'fieldname',
              desc: 'Get values for a field directly',
              example: 'In: {user: "John"} → Out: "John"'
            },
            {
              key: '2',
              pattern: '0.fieldname',
              desc: 'Get field from first item in array',
              example: 'In: [{name: "John"}] → Out: "John"'
            },
            {
              key: '3',
              pattern: 'users.0.name',
              desc: 'Get from nested structure',
              example: 'In: {users: [{name: "John"}]} → Out: "John"'
            }
          ]}
        />

        <Title level={4} style={{ marginTop: 16 }}>Finding Specific Values</Title>
        <Table
          size="small"
          pagination={false}
          columns={[
            { title: 'Pattern', dataIndex: 'pattern', key: 'pattern' },
            { title: 'What It Does', dataIndex: 'desc', key: 'desc' }
          ]}
          dataSource={[
            {
              key: '1',
              pattern: 'id=123.name',
              desc: 'Find item where id is 123, return its name'
            },
            {
              key: '2',
              pattern: 'type="user".email',
              desc: 'Find item where type is "user", return its email'
            }
          ]}
        />

        <Alert
          type="info"
          message="Working with Arrays"
          description={
            <Space direction="vertical">
              <Text>Arrays can be accessed in different ways:</Text>
              <ul style={{ marginLeft: 20 }}>
                <li><Text code>0.name</Text> - First item's name</li>
                <li><Text code>data.0.name</Text> - First item's name in data array</li>
                <li><Text code>id=123.name</Text> - Name where ID matches</li>
              </ul>
            </Space>
          }
          style={{ marginTop: 16 }}
        />

        <Alert
          type="success"
          message="Tips for Success"
          description={
            <ul style={{ marginLeft: 20 }}>
              <li>Look at the API response structure first</li>
              <li>Use dots (.) to go deeper into nested objects</li>
              <li>Use numbers (0, 1, 2) to access array items</li>
              <li>Use field=value to find specific items</li>
              <li>Keep it simple - extract only what you need</li>
            </ul>
          }
          style={{ marginTop: 16 }}
        />

        <Space direction="vertical" style={{ width: '100%', marginTop: 16 }}>
          <Text strong>Common Use Cases:</Text>
          <Card size="small">
            <ul style={{ marginLeft: 20, marginBottom: 0 }}>
              <li><Text code>users.0.name</Text> - Get first user's name from a users array</li>
              <li><Text code>data.items</Text> - Get all items from data object</li>
              <li><Text code>status="active".count</Text> - Get count where status is active</li>
              <li><Text code>type="product".price</Text> - Get price where type is product</li>
            </ul>
          </Card>
        </Space>
      </Space>
    </Modal>
  );
};

const { Option } = Select;
const { TextArea } = Input;
const { Text, Title, Paragraph } = Typography;
const { TabPane } = Tabs;


const APICallNode = ({ id, data, isConnectable }) => {
  // DEFENSIVE INITIALIZATION - This is the key fix!
  const [nodeName, setNodeName] = useState(data.nodeName || `API Call ${id}`);
  const [method, setMethod] = useState(data.method || 'GET');
  const [url, setUrl] = useState(data.url || '');
  const [headers, setHeaders] = useState(ensureArray(data.headers));
  const [body, setBody] = useState(data.body || '');
  const [helpModalVisible, setHelpModalVisible] = useState(false);
  const [queryParams, setQueryParams] = useState(ensureArray(data.queryParams));
  const [auth, setAuth] = useState(ensureObject(data.auth, {
    type: 'none',
    username: '',
    password: '',
    token: '',
    apiKey: '',
    apiKeyName: 'X-API-Key'
  }));
  const [responseConfig, setResponseConfig] = useState(ensureObject(data.responseConfig, {
    jsonPath: '',
    timeout: 30000,
    retryCount: 0,
    cacheResponse: false,
    cacheTime: 300
  }));

  // Test functionality states
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testError, setTestError] = useState(null);
  const [activeTab, setActiveTab] = useState('request');

  useEffect(() => {
    data.updateNodeData(id, {
      nodeName,
      method,
      url,
      headers,
      body,
      queryParams,
      auth,
      responseConfig,
      testResult
    });
  }, [nodeName, method, url, headers, body, queryParams, auth, responseConfig, testResult, id, data]);

  const handleHeaderChange = (index, field, value) => {
    const newHeaders = [...headers];
    newHeaders[index] = { ...newHeaders[index], [field]: value };
    setHeaders(newHeaders);
  };

  const handleQueryParamChange = (index, field, value) => {
    const newParams = [...queryParams];
    newParams[index] = { ...newParams[index], [field]: value };
    setQueryParams(newParams);
  };

  const handleAuthTypeChange = (value) => {
    setAuth({ ...auth, type: value });
  };

  const handleBodyChange = (nodeId, bodyData) => {
    setBody(bodyData.content);
  };

  const renderResponseSection = () => (
    <Space direction="vertical" style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Text>Extract Data Pattern</Text>
        <Button 
          type="link" 
          icon={<QuestionCircleOutlined />}
          onClick={() => setHelpModalVisible(true)}
        >
          View Guide
        </Button>
      </div>
      
      <GenericNodeContent
        id={id}
        data={{ content: responseConfig.jsonPath }}
        onChange={(nodeId, newData) => {
          setResponseConfig(prev => ({
            ...prev,
            jsonPath: newData.content
          }));
        }}
        placeholder="Enter extraction pattern (e.g., price, id=90.price)"
      />
      
      <Text type="secondary" style={{ fontSize: '12px' }}>
        Quick Examples:
        • price (all prices)
        • 0.price (first product's price)
        • id=90.price (price by ID)
        • name="Product Name".price (price by name)
      </Text>

      <div style={{ marginTop: 8 }}>
        <Text>Timeout (ms)</Text>
        <InputNumber
          min={1000}
          max={300000}
          value={responseConfig.timeout}
          onChange={value => setResponseConfig({ ...responseConfig, timeout: value })}
          className="nodrag"
          style={{ width: '100%' }}
        />
      </div>

      <div style={{ marginTop: 8 }}>
        <Text>Retry Count</Text>
        <InputNumber
          min={0}
          max={5}
          value={responseConfig.retryCount}
          onChange={value => setResponseConfig({ ...responseConfig, retryCount: value })}
          className="nodrag"
          style={{ width: '100%' }}
        />
      </div>

      <div style={{ marginTop: 8 }}>
        <Space>
          <Switch
            checked={responseConfig.cacheResponse}
            onChange={checked => setResponseConfig({ ...responseConfig, cacheResponse: checked })}
            className="nodrag"
          />
          <Text>Cache Response</Text>
        </Space>
      </div>

      {responseConfig.cacheResponse && (
        <div style={{ marginTop: 8 }}>
          <Text>Cache Time (seconds)</Text>
          <InputNumber
            min={60}
            max={86400}
            value={responseConfig.cacheTime}
            onChange={value => setResponseConfig({ ...responseConfig, cacheTime: value })}
            className="nodrag"
            style={{ width: '100%' }}
          />
        </div>
      )}
    </Space>
  );

  const renderAuthFields = () => {
    switch (auth.type) {
      case 'basic':
        return (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Input
              placeholder="Username"
              value={auth.username}
              onChange={e => setAuth({ ...auth, username: e.target.value })}
              prefix={<LockOutlined />}
            />
            <Input.Password
              placeholder="Password"
              value={auth.password}
              onChange={e => setAuth({ ...auth, password: e.target.value })}
              prefix={<LockOutlined />}
            />
          </Space>
        );
      case 'bearer':
        return (
          <Input
            placeholder="Bearer Token"
            value={auth.token}
            onChange={e => setAuth({ ...auth, token: e.target.value })}
            prefix={<LockOutlined />}
          />
        );
      case 'apiKey':
        return (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Input
              placeholder="API Key Name"
              value={auth.apiKeyName}
              onChange={e => setAuth({ ...auth, apiKeyName: e.target.value })}
            />
            <Input
              placeholder="API Key Value"
              value={auth.apiKey}
              onChange={e => setAuth({ ...auth, apiKey: e.target.value })}
              prefix={<LockOutlined />}
            />
          </Space>
        );
      default:
        return null;
    }
  };

  const handleTest = async () => {
    setIsTesting(true);
    setTestError(null);
    setTestResult(null);

    try {
      // Build the request configuration
      const testConfig = {
        method,
        url,
        headers: headers.reduce((acc, h) => ({ ...acc, [h.name]: h.value }), {}),
        queryParams: queryParams.reduce((acc, p) => ({ ...acc, [p.key]: p.value }), {}),
        body: body ? JSON.parse(body) : undefined,
        auth: {
            type: auth.type,
            username: auth.type === 'basic' ? auth.username : undefined,
            password: auth.type === 'basic' ? auth.password : undefined,
            token: auth.type === 'bearer' ? auth.token : undefined,
            apiKey: auth.type === 'apiKey' ? auth.apiKey : undefined,
            apiKeyName: auth.type === 'apiKey' ? auth.apiKeyName : undefined
        },
        responseConfig
    };

      const response = await adminInstance.post('/test-api-call', testConfig);
      
      setTestResult(response.data);
      setActiveTab('test'); // Switch to test results tab
    } catch (error) {
      setTestError(error.response?.data?.message || error.message);
    } finally {
      setIsTesting(false);
    }
  };

  const renderTestResults = () => {
    if (isTesting) {
      return (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Spin tip="Testing API call..." />
        </div>
      );
    }

    if (testError) {
      return (
        <Alert
          type="error"
          message="Test Failed"
          description={testError}
          style={{ marginBottom: 16 }}
        />
      );
    }

    if (testResult) {
      return (
        <div>
          <div style={{ marginBottom: 16 }}>
            <Text strong>Status:</Text>{' '}
            <Tag color={testResult.status >= 200 && testResult.status < 300 ? 'success' : 'error'}>
              {testResult.status}
            </Tag>
          </div>

          <Text strong>Response:</Text>
          <div style={{ position: 'relative' }}>
            <TextArea
              value={JSON.stringify(testResult.data, null, 2)}
              autoSize={{ minRows: 4, maxRows: 10 }}
              readOnly
              style={{ marginTop: 8 }}
            />
            <Button
              icon={<CopyOutlined />}
              style={{ position: 'absolute', right: 8, top: 8 }}
              onClick={() => {
                navigator.clipboard.writeText(JSON.stringify(testResult.data, null, 2));
                message.success('Response copied to clipboard');
              }}
            />
          </div>

        </div>
      );
    }

    return (
      <Alert
        type="info"
        message="No test results yet"
        description="Click the 'Test API Call' button to see the results here."
        style={{ marginBottom: 16 }}
      />
    );
  };

  return (
    <>
    <Card
    title={
      <Space>
          <ApiOutlined style={{ fontSize: '18px', color: '#1890ff' }}/>
        <span>API Call</span>
      </Space>
    }
      style={{ width: 480 }}
      extra={
        <Space>
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={handleTest}
            loading={isTesting}
            disabled={!url}
          >
            Test
          </Button>
          <Popconfirm
        title="Delete Node"
        description="Are you sure you want to delete this node?"
        onConfirm={() => data.onDelete(id)}
        okText="Delete"
        cancelText="Cancel"
        okButtonProps={{ danger: true }} > 
        <Button icon={<DeleteOutlined />} /> </Popconfirm>
        </Space>
      }
    >
      <Handle type="target" position={Position.Left} isConnectable={isConnectable} />
      
      <Input
        placeholder="Node Name"
        value={nodeName}
        onChange={e => setNodeName(e.target.value)}
        style={{ marginBottom: 8 }}
        className="nodrag"
      />

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab="Request" key="request">
          <Space direction="vertical" style={{ width: '100%' }}>
            <Select 
              value={method}
              onChange={setMethod}
              style={{ width: '100%' }}
              className="nodrag"
            >
              {['GET', 'POST', 'PUT', 'DELETE', 'PATCH', 'HEAD', 'OPTIONS'].map(m => (
                <Option key={m} value={m}>{m}</Option>
              ))}
            </Select>

            <Input
              placeholder="URL"
              value={url}
              onChange={e => setUrl(e.target.value)}
              className="nodrag"
            />
            <NodeInputHandler
              nodeId={id}
              onInsert={tag => setUrl(prev => prev + tag)}
            />

            {/* Query Parameters with improved layout */}
            <Text strong>Query Parameters</Text>
            {Array.isArray(queryParams) && queryParams.map((param, index) => (
              <div key={index} style={{ marginBottom: 8 }}>
                <div style={{ display: 'flex', gap: '8px', marginBottom: 4 }}>
                  <Input
                    placeholder="Key"
                    value={param.key || ''}
                    onChange={e => handleQueryParamChange(index, 'key', e.target.value)}
                    style={{ flex: 1 }}
                    className="nodrag"
                  />
                  <Button 
                    icon={<MinusCircleOutlined />}
                    onClick={() => setQueryParams(params => params.filter((_, i) => i !== index))}
                  />
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <TextArea
                    placeholder="Value"
                    value={param.value || ''}
                    onChange={e => handleQueryParamChange(index, 'value', e.target.value)}
                    style={{ flex: 1 }}
                    autoSize={{ minRows: 1, maxRows: 3 }}
                    className="nodrag"
                  />
                  <NodeInputHandler
                    nodeId={id}
                    onInsert={tag => handleQueryParamChange(index, 'value', (param.value || '') + tag)}
                  />
                </div>
              </div>
            ))}
            <Button 
              type="dashed"
              onClick={() => setQueryParams([...queryParams, { key: '', value: '' }])}
              icon={<PlusOutlined />}
              style={{ width: '100%' }}
            >
              Add Query Parameter
            </Button>
          </Space>
        </TabPane>

        <TabPane tab="Headers" key="headers">
        {Array.isArray(headers) && headers.map((header, index) => (
          <div key={index} style={{ marginBottom: 16 }}>
            {/* Header Name and Delete Button */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: 8 }}>
              <Input
                placeholder="Header Name"
                value={header.name || ''}
                onChange={e => handleHeaderChange(index, 'name', e.target.value)}
                style={{ flex: 1 }}
                className="nodrag"
              />
              <Button 
                icon={<MinusCircleOutlined />}
                onClick={() => setHeaders(headers => headers.filter((_, i) => i !== index))}
              />
            </div>
            
            {/* Header Value and Input Handler */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <TextArea
                placeholder="Value"
                value={header.value || ''}
                onChange={e => handleHeaderChange(index, 'value', e.target.value)}
                style={{ width: '100%' }}
                autoSize={{ minRows: 1, maxRows: 3 }}
                className="nodrag"
              />
              <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <NodeInputHandler
                  nodeId={id}
                  onInsert={tag => handleHeaderChange(index, 'value', (header.value || '') + tag)}
                />
              </div>
            </div>
          </div>
        ))}
        <Button 
          type="dashed"
          onClick={() => setHeaders([...headers, { name: '', value: '' }])}
          icon={<PlusOutlined />}
          style={{ width: '100%' }}
        >
          Add Header
        </Button>
        </TabPane>

        <TabPane tab="Body" key="body">
          <GenericNodeContent
            id={id}
            data={{ content: body }}
            onChange={handleBodyChange}
            placeholder="Request Body (JSON)"
          />
        </TabPane>

        <TabPane tab="Auth" key="auth">
          <Space direction="vertical" style={{ width: '100%' }}>
            <Select
              value={auth.type}
              onChange={handleAuthTypeChange}
              style={{ width: '100%' }}
              className="nodrag"
            >
              <Option value="none">No Auth</Option>
              <Option value="basic">Basic Auth</Option>
              <Option value="bearer">Bearer Token</Option>
              <Option value="apiKey">API Key</Option>
            </Select>
            {renderAuthFields()}
          </Space>
        </TabPane>

        <TabPane tab="Response" key="response">
            {renderResponseSection()}
          </TabPane>

        <TabPane tab="Test" key="test">
          {renderTestResults()}
        </TabPane>
      </Tabs>

      <Handle type="source" position={Position.Right} id="a" isConnectable={isConnectable} />
    </Card>
    <ExtractionHelpModal 
        visible={helpModalVisible}
        onClose={() => setHelpModalVisible(false)}
      />
    </>
  );
};

export default APICallNode;